import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { mainNavLinks } from "@/mocks/navigation";
import { LOGO_URL, CATALOG_URL, SONGBO_URL, BRAND_SHORT } from "@/mocks/site-config";
import LanguageSwitcher from "./LanguageSwitcher";
import LocalizedNavLabel from "./LocalizedNavLabel";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  const isActive = (href: string) => {
    if (href === "/") return isHomePage;
    return location.pathname === href;
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleNavClick = (href: string) => {
    if (location.pathname === href || (href === "/" && isHomePage)) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  const linkBase =
    "relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 rounded-sm";

  const linkClass = (active: boolean) => {
    if (scrolled || isHomePage) {
      return active
        ? "text-white"
        : "text-white/65 hover:text-white";
    }
    return active
      ? "text-foreground-900"
      : "text-foreground-600 hover:text-primary-600";
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/15 backdrop-blur-xl"
          : isHomePage
            ? "bg-transparent"
            : "bg-background-50/97 backdrop-blur-md"
      }`}
    >
      <div
        className={`mx-auto flex items-center justify-between px-6 md:px-10 transition-all duration-300 ${
          scrolled ? "h-14 md:h-[56px]" : "h-16 md:h-[68px]"
        }`}
      >
        {/* Logo + Brand Name */}
        <Link
          to="/"
          onClick={() => handleNavClick("/")}
          className="flex items-center gap-3 cursor-pointer flex-shrink-0"
        >
          <img
            src={LOGO_URL}
            alt="崧燁文化事業有限公司 Sunya"
            className={`object-contain transition-all duration-300 ${
              scrolled ? "h-7 md:h-8" : "h-8 md:h-9"
            }`}
          />
          <span
            className={`hidden md:block font-heading font-semibold tracking-[0.03em] whitespace-nowrap transition-all duration-300 ${
              scrolled || isHomePage ? "text-sm text-white" : "text-[15px] text-foreground-900"
            }`}
          >
            {BRAND_SHORT}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center">
          <div className="flex items-center gap-0">
            {mainNavLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className={`${linkBase} ${linkClass(active)}`}
                >
                  <LocalizedNavLabel href={link.href} fallback={link.label} />
                  {active && (
                    <span
                      className={`absolute bottom-0 left-4 right-4 h-[2px] transition-colors duration-300 ${
                        scrolled || isHomePage ? "bg-primary-400" : "bg-primary-500"
                      }`}
                    ></span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* External Portals */}
          <div className={`ml-5 pl-5 border-l transition-colors duration-300 ${
            scrolled || isHomePage ? "border-primary-400/25" : "border-primary-300/30"
          }`}>
            <div className="flex items-center gap-1">
              <a
                href={SONGBO_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 rounded-sm ${
                  scrolled || isHomePage
                    ? "text-white/55 hover:text-primary-300"
                    : "text-foreground-500 hover:text-primary-600"
                }`}
              >
                崧博出版
                <i className="ri-external-link-line text-[11px]"></i>
              </a>
              <span className={`text-[10px] transition-colors duration-300 ${
                scrolled || isHomePage ? "text-white/20" : "text-foreground-300"
              }`}>·</span>
              <a
                href={CATALOG_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 rounded-sm ${
                  scrolled || isHomePage
                    ? "text-white/55 hover:text-primary-300"
                    : "text-foreground-500 hover:text-primary-600"
                }`}
              >
                書籍資料庫
                <i className="ri-external-link-line text-[11px]"></i>
              </a>
            </div>
          </div>
        </div>

        <LanguageSwitcher />

        {/* Mobile Toggle */}
        <button
          className="lg:hidden flex items-center justify-center w-11 h-11 cursor-pointer -mr-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "關閉選單" : "開啟選單"}
          aria-expanded={mobileOpen}
        >
          <i
            className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl transition-colors duration-300 ${
              scrolled || isHomePage ? "text-white" : "text-foreground-800"
            }`}
          ></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className={`lg:hidden px-6 py-5 flex flex-col max-h-[75vh] overflow-y-auto ${
            scrolled
              ? "bg-black/80 backdrop-blur-xl"
              : isHomePage
                ? "bg-black/80 backdrop-blur-xl"
                : "bg-background-50"
          }`}
          role="menu"
        >
          {mainNavLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => handleNavClick(link.href)}
                role="menuitem"
                className={`block text-[15px] font-medium py-3.5 px-3 transition-colors cursor-pointer ${
                  active
                    ? scrolled || isHomePage
                      ? "text-white bg-white/10 border-l-2 border-primary-400 pl-[10px]"
                      : "text-foreground-900 bg-background-100 border-l-2 border-primary-500 pl-[10px]"
                    : scrolled || isHomePage
                      ? "text-white/65 hover:text-white hover:bg-white/10 border-l-2 border-transparent pl-3"
                      : "text-foreground-600 hover:text-primary-600 hover:bg-background-100 border-l-2 border-transparent pl-3"
                }`}
              >
                <LocalizedNavLabel href={link.href} fallback={link.label} />
              </Link>
            );
          })}

          <div className={`mt-5 pt-5 border-t transition-colors duration-300 ${
            scrolled || isHomePage ? "border-white/10" : "border-background-200"
          }`}>
            <a
              href={SONGBO_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              role="menuitem"
              className={`block text-[15px] font-medium py-3.5 px-3 transition-colors cursor-pointer ${
                scrolled || isHomePage
                  ? "text-white/55 hover:text-primary-300"
                  : "text-foreground-500 hover:text-primary-600"
              }`}
            >
              崧博出版
              <i className="ri-external-link-line text-xs ml-1.5"></i>
            </a>
            <a
              href={CATALOG_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              role="menuitem"
              className={`block text-[15px] font-medium py-3.5 px-3 transition-colors cursor-pointer ${
                scrolled || isHomePage
                  ? "text-white/55 hover:text-primary-300"
                  : "text-foreground-500 hover:text-primary-600"
              }`}
            >
              書籍資料庫
              <i className="ri-external-link-line text-xs ml-1.5"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}